// src/components/CalendarView/CalendarView.tsx
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { dashboardApi } from '@/services/dashboardApi';
import { conversationBus } from '@/lib/conversationBus';
import { authStorage } from '@/lib/storage';
import { config as envConfig } from '@/config/env';
import { socketService } from '@/services/socketService';
import { CalendarHeader } from './CalendarHeader';
import { CalendarMonthView } from './CalendarMonthView';
import { CalendarWeekView } from './CalendarWeekView';
import { CalendarDayView } from './CalendarDayView';
import DuplicateCard from '@/components/DuplicateCard/DuplicateCard';
import type { DuplicateCardRef } from '@/components/DuplicateCard/DuplicateCard';
import type { CalendarEvent, CalendarViewMode, CalendarViewProps, ActionMenuConfig } from './types';
import { dedupeCalendarEvents } from './calendarUtils';
import './CalendarView.css';

function formatDateYMD(date: Date): string
{
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');

  return `${y}${m}${d}`;
}

function getDateRange(mode: CalendarViewMode, date: Date): { start: Date; end: Date }
{
  if (mode === 'month')
  {
    const start = new Date(date.getFullYear(), date.getMonth(), 1);
    const end = new Date(date.getFullYear(), date.getMonth() + 1, 1);

    return { start, end };
  }
  if (mode === 'week')
  {
    const start = new Date(date);

    start.setDate(start.getDate() - start.getDay());
    const end = new Date(start);

    end.setDate(end.getDate() + 7);
    return { start, end };
  }
  const start = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const end = new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1);

  return { start, end };
}

function buildActionMenuConfig(): ActionMenuConfig
{
  const token = authStorage.getAccessToken() || '';
  const webUrl = import.meta.env.VITE_WEB_URL || 'http://localhost/Corgee.WebClient/';
  const apiUrl =
    import.meta.env.VITE_API_URL ||
    import.meta.env.VITE_JS_apiURL ||
    'http://localhost/Corgee.Web/';

  let storageData: Record<string, any> = {};

  try
  {
    storageData = JSON.parse(sessionStorage.getItem('C2_data') || '{}');
  }
  catch
  {
    /* ignore */
  }

  let allLayers: any[] = [];

  try
  {
    allLayers = JSON.parse(sessionStorage.getItem('C2_AllLayers') || '[]');
  }
  catch
  {
    /* ignore */
  }

  let layersToHide: any[] = [];

  try
  {
    layersToHide = JSON.parse(sessionStorage.getItem('C2_LayersToHide') || '[]');
  }
  catch
  {
    /* ignore */
  }

  return {
    api: { url: envConfig.api.baseUrl, token },
    config: { weburl: webUrl, apiurl: apiUrl },
    storageData: {
      data: storageData,
      allLayers,
      layersToHide,
      layerId: parseInt(localStorage.getItem('C2_layerid') || '0'),
      conv_maxAllowedUpload: 20480000,
      enableChat: localStorage.getItem('C2_enableChat') === 'true',
      personid: parseInt(localStorage.getItem('C2_personid') || '0'),
    },
    configMenus: {
      conversation: [
        'goto-project',
        'close-infowindow',
        'opendetails-newtab',
        'mark-redflag',
        'mark-important',
        'follow-conv',
        'create-itemtype',
        'convert-to',
        'duplicate-project',
        'copylink-to-clipboard',
        'move-to',
        'copy-to',
        'submit-time',
        'edit-history',
        'edit',
        'delete',
        'duplicate-item',
        'archive',
      ],
      reply: ['mark-favorite', 'repost', 'delete', 'create-itemtype'],
    },
    realItemTypes: {
      Users: { Name: 'People', Icon: 'icons-menu-account-box' },
      Tasks: { Name: 'Tasks', Icon: 'icons-menu-epic' },
    },
    userData: {
      imageProfile: 'Images/user.png?1.0.21.8',
      personName: 'User',
    },
  };
}

export const CalendarView: React.FC<CalendarViewProps> = ({
  dashboardId,
  projectId,
  filters = '[]',
  searchText = '',
  layersToHide = '',
  extraFilterParams,
  onOpenInfoWindow,
  onRegisterRefresh,
}) =>
{
  const [calViewMode, setCalViewMode] = useState<CalendarViewMode>('month');
  const [currentDate, setCurrentDate] = useState<Date>(() => new Date());
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Built once — auth tokens are stable per session
  const actionMenuConfig = useMemo(() => buildActionMenuConfig(), []);

  const duplicateCardRef = useRef<DuplicateCardRef>(null);
  const duplicateCardInitRef = useRef<((conv: any) => void) | null>(null);

  const handleDuplicateConversation = useCallback((_e: any, conv: any) =>
  {
    duplicateCardInitRef.current?.(conv);
    duplicateCardRef.current?.openModal();
  }, []);

  const filtersRef = useRef(filters);
  const searchTextRef = useRef(searchText);
  const layersToHideRef = useRef(layersToHide);

  useEffect(() =>
  {
    filtersRef.current = filters;
  }, [filters]);
  useEffect(() =>
  {
    searchTextRef.current = searchText;
  }, [searchText]);
  useEffect(() =>
  {
    layersToHideRef.current = layersToHide;
  }, [layersToHide]);

  const extraFilterParamsRef = useRef(extraFilterParams);

  useEffect(() =>
  {
    extraFilterParamsRef.current = extraFilterParams;
  }, [extraFilterParams]);

  // Every load takes a ticket; only the most recent one may write state.
  // Without this, switching project/dashboard leaves the previous request in
  // flight and, if it resolves last, it overwrites events with the old
  // project's data - which is why the calendar sometimes showed foreign cards.
  const requestIdRef = useRef(0);

  const loadEvents = useCallback(
    async (mode: CalendarViewMode, date: Date) =>
    {
      const requestId = ++requestIdRef.current;
      const requestedDashboardId = dashboardId;

      setIsLoading(true);
      try
      {
        const { start, end } = getDateRange(mode, date);
        const evts = await dashboardApi.getTasksCalendar({
          CustomFields: [],
          PageSize: 500,
          PageNumber: 1,
          StartDate: formatDateYMD(start),
          EndDate: formatDateYMD(end),
          DashboardId: requestedDashboardId,
          // Without Project the API returns events from every project that
          // shares the dashboard, unlike the card/grid views.
          Project: projectId ?? '',
          Filters: filtersRef.current,
          SearchText: searchTextRef.current,
          LayersToHide: layersToHideRef.current,
          ...extraFilterParamsRef.current,
        });

        // A newer load started while this one was in flight - discard
        if (requestId !== requestIdRef.current)
        {
          return;
        }

        setEvents(dedupeCalendarEvents(evts));
      }
      catch (err)
      {
        if (requestId !== requestIdRef.current)
        {
          return;
        }
        console.error('CalendarView: failed to load events', err);
        setEvents([]);
      }
      finally
      {
        if (requestId === requestIdRef.current)
        {
          setIsLoading(false);
        }
      }
    },
    [dashboardId, projectId],
  );

  // extraFilterParams is rebuilt by the parent on every render, so depending on
  // its identity fired a fetch per render (3 per mount, observed). Depend on
  // its contents instead.
  const extraFilterParamsKey = useMemo(
    () => JSON.stringify(extraFilterParams ?? null),
    [extraFilterParams],
  );

  useEffect(() =>
  {
    loadEvents(calViewMode, currentDate);
  }, [
    loadEvents,
    calViewMode,
    currentDate,
    filters,
    searchText,
    layersToHide,
    extraFilterParamsKey,
  ]);

  const reload = useCallback(() =>
  {
    loadEvents(calViewMode, currentDate);
  }, [loadEvents, calViewMode, currentDate]);

  useEffect(() =>
  {
    onRegisterRefresh?.(reload);
  }, [onRegisterRefresh, reload]);

  // Keep stable refs so bus callbacks never have stale closures
  const eventsRef = useRef(events);
  const loadEventsRef = useRef(loadEvents);

  useEffect(() =>
  {
    eventsRef.current = events;
  }, [events]);
  useEffect(() =>
  {
    loadEventsRef.current = loadEvents;
  }, [loadEvents]);

  // title-updated: update the matching event title in-place (no reload needed)
  useEffect(() =>
  {
    return conversationBus.on('title-updated', ({ conversationId, title }) =>
    {
      setEvents(prev =>
        prev.map(ev =>
          ev.ConversationId === conversationId
            ? { ...ev, Conversation: { ...ev.Conversation, Title: title } }
            : ev,
        ),
      );
    });
  }, []);

  // layer-updated: update date events in-place — no API call needed
  const calViewModeRef = useRef(calViewMode);
  const currentDateRef = useRef(currentDate);

  useEffect(() =>
  {
    calViewModeRef.current = calViewMode;
  }, [calViewMode]);
  useEffect(() =>
  {
    currentDateRef.current = currentDate;
  }, [currentDate]);

  useEffect(() =>
  {
    return conversationBus.on('layer-updated', ({ conversationId, layerData }) =>
    {
      // FieldTypeId 6 = InputDateField (date picker) in the CustomFields system
      const dateFields: any[] =
        (layerData as any)?.CustomFields?.filter((f: any) => f.FieldTypeId === 6) ?? [];

      if (dateFields.length === 0)
      {
        return;
      }

      // Use the layer color as a proxy for layer identity,
      // since CalendarEvent doesn't carry LayerId but does carry Color.
      const layerColor: string = (layerData as any)?.Color ?? '';

      // Check visibility at layer level: conversation must exist AND have events from this specific layer.
      // If the conversation exists in other layers but not this one, we still need a reload.
      const isLayerVisible = eventsRef.current.some(
        ev => ev.ConversationId === conversationId && (!layerColor || ev.Color === layerColor),
      );

      if (!isLayerVisible)
      {
        loadEventsRef.current(calViewModeRef.current, currentDateRef.current);
        return;
      }

      const { start, end } = getDateRange(calViewModeRef.current, currentDateRef.current);

      // A field that was empty has no event row to patch - the API never
      // returned one for it - so the in-place update below would silently do
      // nothing. Reload instead, which is the only way to get a complete
      // event (Conversation, Color, CustomFieldId, ...) for the new date.
      const conversationEvents = eventsRef.current.filter(
        ev =>
          ev.ConversationId === conversationId &&
          (!layerColor || !ev.Color || ev.Color === layerColor),
      );

      const hasNewlyFilledDate = dateFields.some(f =>
      {
        const rawValue = f.Value;

        if (!rawValue || rawValue === 'Not Set')
        {
          return false;
        }

        const newDate = new Date(rawValue);

        if (isNaN(newDate.getTime()))
        {
          return false;
        }

        // Outside the visible range there is nothing to render either way
        if (newDate < start || newDate >= end)
        {
          return false;
        }

        return !conversationEvents.some(ev => ev.CustomFieldName === f.Name);
      });

      if (hasNewlyFilledDate)
      {
        loadEventsRef.current(calViewModeRef.current, currentDateRef.current);
        return;
      }

      setEvents(prev =>
      {
        let changed = false;
        const next = prev.reduce<CalendarEvent[]>((acc, ev) =>
        {
          if (ev.ConversationId !== conversationId)
          {
            acc.push(ev);
            return acc;
          }

          // Match by field name AND layer color to avoid updating events from a different layer
          const colorMatches = !layerColor || !ev.Color || ev.Color === layerColor;
          const field = colorMatches
            ? dateFields.find(f => f.Name === ev.CustomFieldName)
            : undefined;

          if (!field)
          {
            acc.push(ev);
            return acc;
          }

          // Parse the new date — InputDateField stores as "MM/DD/YYYY"
          const rawValue = field.Value;
          const newDate = rawValue && rawValue !== 'Not Set' ? new Date(rawValue) : null;

          changed = true;

          if (!newDate || isNaN(newDate.getTime()))
          {
            // Date cleared → remove this event
            return acc;
          }

          if (newDate < start || newDate >= end)
          {
            // Date moved outside current view → remove
            return acc;
          }

          // Build a local-time ISO string consistent with what the API returns
          const y = newDate.getFullYear();
          const m = String(newDate.getMonth() + 1).padStart(2, '0');
          const d = String(newDate.getDate()).padStart(2, '0');

          acc.push({ ...ev, Date: `${y}-${m}-${d}T00:00:00` });
          return acc;
        }, []);

        return changed ? dedupeCalendarEvents(next) : prev;
      });
    });
  }, []);

  const handlePrev = useCallback(() =>
  {
    setCurrentDate(prev =>
    {
      const d = new Date(prev);

      if (calViewMode === 'month')
      {
        d.setMonth(d.getMonth() - 1);
      }
      else if (calViewMode === 'week')
      {
        d.setDate(d.getDate() - 7);
      }
      else
      {
        d.setDate(d.getDate() - 1);
      }
      return d;
    });
  }, [calViewMode]);

  const handleNext = useCallback(() =>
  {
    setCurrentDate(prev =>
    {
      const d = new Date(prev);

      if (calViewMode === 'month')
      {
        d.setMonth(d.getMonth() + 1);
      }
      else if (calViewMode === 'week')
      {
        d.setDate(d.getDate() + 7);
      }
      else
      {
        d.setDate(d.getDate() + 1);
      }
      return d;
    });
  }, [calViewMode]);

  const handleToday = useCallback(() =>
  {
    setCurrentDate(new Date());
  }, []);

  const handleOpenInfoWindow = useCallback(
    (conversationId: number) =>
    {
      onOpenInfoWindow?.(conversationId);
    },
    [onOpenInfoWindow],
  );

  const sharedViewProps = {
    events,
    currentDate,
    isLoading,
    onOpenInfoWindow: handleOpenInfoWindow,
    actionMenuConfig,
    onDuplicateItem: handleDuplicateConversation,
  };

  return (
    <div className="cal-root">
      <CalendarHeader
        calViewMode={calViewMode}
        currentDate={currentDate}
        onViewModeChange={setCalViewMode}
        onPrev={handlePrev}
        onNext={handleNext}
        onToday={handleToday}
      />
      <div className="cal-body">
        {calViewMode === 'month' && <CalendarMonthView {...sharedViewProps} />}
        {calViewMode === 'week' && <CalendarWeekView {...sharedViewProps} />}
        {calViewMode === 'day' && <CalendarDayView {...sharedViewProps} />}
      </div>
      <DuplicateCard
        ref={duplicateCardRef}
        config={{
          apiUrl: actionMenuConfig.api.url,
          authToken: actionMenuConfig.api.token,
          SocketConnectionId: socketService.connectionId,
          attachReactFunctions: (fns: any) =>
          {
            duplicateCardInitRef.current = fns.initConfig;
          },
          DashboardId: 0,
          functions: {
            loadingBarStart: () => {},
            loadingBarComplete: () => {},
            addConversation: () =>
            {
              reload();
            },
          },
          params: { Conversation: {} },
        }}
        UpdateCustomFields={() => {}}
      />
    </div>
  );
};

export default CalendarView;
